import React from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  width: 90vw;
  max-width: 1200px;
  height: 90vh;
  background: linear-gradient(135deg, rgba(0, 20, 40, 0.98), rgba(20, 0, 40, 0.98));
  border: 3px solid ${props => props.borderColor || '#00ffff'};
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 255, 255, 0.3);
  animation: slideUp 0.4s ease;

  @keyframes slideUp {
    from { 
      transform: translateY(50px);
      opacity: 0;
    }
    to { 
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  padding: 24px 32px;
  background: rgba(0, 0, 0, 0.6);
  border-bottom: 2px solid ${props => props.borderColor || '#00ffff'};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  color: ${props => props.color || '#00ffff'};
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 0 20px ${props => props.color || '#00ffff'}66;
  max-width: 900px;
`;

const CloseButton = styled.button`
  background: none;
  border: 2px solid ${props => props.color || '#00ffff'};
  color: ${props => props.color || '#00ffff'};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.color || '#00ffff'};
    color: #000000;
    transform: rotate(90deg);
  }
`;

const MetaBar = styled.div`
  padding: 16px 32px;
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid ${props => props.borderColor || '#00ffff'}33;
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  color: ${props => props.color || '#00ffff'};
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;

  strong {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

const ArticleBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 255, 255, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.scrollColor || '#00ffff'};
    border-radius: 4px;
  }
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  color: ${props => props.color || '#00ffff'};
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 2px;
  border-bottom: 2px solid ${props => props.color || '#00ffff'}44;
  padding-bottom: 8px;
`;

const Paragraph = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  line-height: 1.8;
  margin-bottom: 16px;
`;

const KeywordTag = styled.span`
  display: inline-block;
  background: ${props => props.color || '#00ffff'}22;
  color: ${props => props.color || '#00ffff'};
  border: 1px solid ${props => props.color || '#00ffff'};
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  margin-right: 8px;
  margin-bottom: 8px;
  font-weight: 600;
`;

const ReferenceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.color || '#00ffff'};
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 16px;
  border: 2px solid ${props => props.color || '#00ffff'};
  border-radius: 6px;
  transition: all 0.2s;
  margin-right: 12px;
  margin-bottom: 12px;

  &:hover {
    background: ${props => props.color || '#00ffff'};
    color: #000000;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => props.color || '#00ffff'}66;
  }
`;

const Abstract = styled.div`
  background: rgba(0, 255, 255, 0.05);
  border-left: 4px solid ${props => props.color || '#00ffff'};
  padding: 20px;
  margin-bottom: 24px;
  color: rgba(255, 255, 255, 0.95);
  font-size: 16px;
  line-height: 1.7;
  font-style: italic;
`;

const ArticleModal = ({ node, onClose }) => {
  if (!node) return null;

  const nodeColor = node.color || '#00ffff';

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent 
        borderColor={nodeColor}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader borderColor={nodeColor}>
          <Title color={nodeColor}>{node.label || node.title}</Title>
          <CloseButton color={nodeColor} onClick={onClose}>
            ×
          </CloseButton>
        </ModalHeader>

        <MetaBar borderColor={nodeColor}>
          <MetaItem color={nodeColor}>
            <strong>Date:</strong> {new Date(node.date).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </MetaItem>
          <MetaItem color={nodeColor}>
            <strong>Group:</strong> {node.group}
          </MetaItem>
          <MetaItem color={nodeColor}>
            <strong>Recursion Depth:</strong> {node.recursion_depth || 0}
          </MetaItem>
          <MetaItem color={nodeColor}>
            <strong>Node ID:</strong> {node.id}
          </MetaItem>
        </MetaBar>

        <ArticleBody scrollColor={nodeColor}>
          {node.abstract && (
            <Abstract color={nodeColor}>
              {node.abstract}
            </Abstract>
          )}

          {node.keywords && node.keywords.length > 0 && (
            <Section>
              <SectionTitle color={nodeColor}>Keywords</SectionTitle>
              <div>
                {node.keywords.map((keyword, i) => (
                  <KeywordTag key={i} color={nodeColor}>
                    {keyword}
                  </KeywordTag>
                ))}
              </div>
            </Section>
          )}

          {node.full_text && (
            <Section>
              <SectionTitle color={nodeColor}>Full Article</SectionTitle>
              <Paragraph>{node.full_text}</Paragraph>
            </Section>
          )}

          {node.sections && (
            <>
              {node.sections.introduction && (
                <Section>
                  <SectionTitle color={nodeColor}>Introduction</SectionTitle>
                  <Paragraph>{node.sections.introduction}</Paragraph>
                </Section>
              )}

              {node.sections.main && node.sections.main.length > 0 && (
                node.sections.main.map((section, i) => (
                  <Section key={i}>
                    <SectionTitle color={nodeColor}>{section.heading}</SectionTitle>
                    <Paragraph>{section.content}</Paragraph>
                  </Section>
                ))
              )}

              {node.sections.conclusion && (
                <Section>
                  <SectionTitle color={nodeColor}>Conclusion</SectionTitle>
                  <Paragraph>{node.sections.conclusion}</Paragraph>
                </Section>
              )}
            </>
          )}

          <Section>
            <SectionTitle color={nodeColor}>References & Links</SectionTitle>
            <div>
              <ReferenceLink 
                color={nodeColor}
                href={`https://recursivearchitectureintelligence.com/research#${node.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                📄 View on RAI Website
              </ReferenceLink>
              
              {node.pdf_url && (
                <ReferenceLink 
                  color={nodeColor}
                  href={node.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  📑 Download PDF
                </ReferenceLink>
              )}

              {node.reference_url && (
                <ReferenceLink 
                  color={nodeColor}
                  href={node.reference_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  🔗 Scientific Reference
                </ReferenceLink>
              )}
            </div>
          </Section>

          {node.geometry && (
            <Section>
              <SectionTitle color={nodeColor}>Geometric Properties</SectionTitle>
              <Paragraph>
                <strong>Drift Magnitude:</strong> {node.geometry.drift_magnitude?.toFixed(3) || 'N/A'}<br/>
                <strong>Curvature Range:</strong> {node.geometry.curvature_range?.toFixed(3) || 'N/A'}<br/>
                <strong>Complexity:</strong> {node.geometry.complexity?.toFixed(3) || 'N/A'}
              </Paragraph>
            </Section>
          )}
        </ArticleBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ArticleModal;